<template>
  <b-card>
    <!-- Confirm Password Modal -->
    <b-modal
      v-model="confirmModal.show"
      title="Confirm Action"
      centered
      hide-footer
    >
      <p class="text-black">
        Enter your password to
        <strong>{{ confirmModal.label }}</strong>
      </p>
      <b-form-input
        v-model="confirmModal.password"
        type="password"
        placeholder="Enter password"
        class="mb-2"
        @keyup.enter="applyToggle"
      />
      <div class="d-flex justify-content-end gap-2">
        <b-button variant="outline-secondary" class="mr-1" @click="cancelToggle">Cancel</b-button>
        <b-button variant="danger" :disabled="saving" @click="applyToggle">
          <b-spinner v-if="saving" small class="mr-50" />
          Confirm
        </b-button>
      </div>
    </b-modal>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="font-weight-bolder">Maintenance &amp; Updates</h5>
      <!-- Schedule Maintenance: no API — disabled -->
      <b-button
        variant="outline-secondary"
        class="text-danger font-weight-bolder"
        disabled
        title="Not available yet"
      >
        <feather-icon icon="ClockIcon" />
        Schedule Maintenance
      </b-button>
    </div>

    <!-- Scheduled Maintenance: no API — greyed out -->
    <b-row class="mt-3">
      <b-col cols="12">
        <h6 class="font-weight-bold section-na-label">
          Scheduled Maintenance
          <b-badge variant="secondary" class="ml-1 font-weight-normal" style="font-size: 10px">Not available</b-badge>
        </h6>
      </b-col>
      <b-col cols="12">
        <div class="section-disabled">
          <b-card
            class="rounded-lg"
            style="background: #fefce8; border: 1px solid #fff085"
          >
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>Database Optimization</strong>
                <div class="text-muted">
                  Scheduled for Sunday, 2:00 AM - 4:00 AM AEST
                </div>
              </div>
              <b-button size="sm" variant="light" disabled>Edit</b-button>
            </div>
          </b-card>
        </div>
      </b-col>
    </b-row>

    <!-- System Updates: no API — greyed out -->
    <b-row>
      <b-col cols="12">
        <h6 class="mb-2 section-na-label">
          System Updates
          <b-badge variant="secondary" class="ml-1 font-weight-normal" style="font-size: 10px">Not available</b-badge>
        </h6>
      </b-col>
      <b-col cols="12">
        <div class="section-disabled">
          <b-card border-variant="secondary" class="rounded-lg">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>Security Patch v2.1.3</strong>
                <div class="text-muted">Critical security updates available</div>
              </div>
              <b-button size="sm" variant="danger" disabled>Install Now</b-button>
            </div>
          </b-card>

          <b-card border-variant="secondary" class="rounded-lg">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>Feature Update v2.2.0</strong>
                <div class="text-muted">
                  New betting features and UI improvements
                </div>
              </div>
              <b-button size="sm" variant="light" disabled>Schedule</b-button>
            </div>
          </b-card>
        </div>
      </b-col>
    </b-row>

    <!-- System Admin Controls -->
    <b-row class="mt-1">
      <b-col cols="12">
        <h6 class="font-weight-bold">System Admin Controls</h6>
      </b-col>
      <b-col cols="12">
        <div v-if="loading" class="text-center py-3">
          <b-spinner variant="primary" />
        </div>
        <b-card v-else border-variant="secondary" class="rounded-lg">
          <!-- Maintenance Mode: has API -->
          <b-form-group class="mb-2">
            <label class="font-weight-bolder">Maintenance Mode</label>
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-muted">Temporarily disable site access</span>
              <b-form-checkbox
                v-model="maintenanceMode"
                switch
                :disabled="saving"
                @change="onToggle('maintenanceMode', $event)"
              />
            </div>
          </b-form-group>

          <!-- Debug Mode: no API — greyed out -->
          <b-form-group class="mb-0">
            <div class="section-disabled">
              <label class="font-weight-bolder">
                Debug Mode
                <b-badge variant="secondary" class="ml-1 font-weight-normal" style="font-size: 10px">Not available</b-badge>
              </label>
              <div class="d-flex align-items-center justify-content-between">
                <span class="text-muted">Enable detailed error logging</span>
                <b-form-checkbox
                  v-model="debugMode"
                  switch
                  disabled
                />
              </div>
            </div>
          </b-form-group>
        </b-card>
      </b-col>
    </b-row>

    <!-- Backup Status: no API — greyed out -->
    <b-row>
      <b-col cols="12">
        <h6 class="mb-2 section-na-label">
          Backup Status
          <b-badge variant="secondary" class="ml-1 font-weight-normal" style="font-size: 10px">Not available</b-badge>
        </h6>
      </b-col>
      <div class="section-disabled w-100 px-1">
        <b-col md="6">
          <b-card border-variant="secondary">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <strong>Database Backup</strong>
                <div class="text-muted small">Last backup: 2 hours ago</div>
                <div class="text-muted small">Next: Tonight at 2:00 AM</div>
              </div>
              <span class="status-dot bg-success"></span>
            </div>
          </b-card>
        </b-col>
        <b-col md="6">
          <b-card border-variant="secondary">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <strong>File System Backup</strong>
                <div class="text-muted small">Last backup: 6 hours ago</div>
                <div class="text-muted small">Next: Daily at 1:00 AM</div>
              </div>
              <span class="status-dot bg-success"></span>
            </div>
          </b-card>
        </b-col>
      </div>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BRow,
  BCol,
  BModal,
  BFormInput,
  BFormCheckbox,
  BFormGroup,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";

export default {
  components: {
    BCard,
    BButton,
    BRow,
    BCol,
    BModal,
    BFormInput,
    BFormCheckbox,
    BFormGroup,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      maintenanceMode: false,
      debugMode: false,
      confirmModal: {
        show: false,
        field: null,
        nextValue: null,
        label: "",
        password: "",
      },
    };
  },
  computed: {
    ...mapState("settings/maintenance", ["loading", "saving"]),
  },
  created() {
    this.fetchConfig();
  },
  methods: {
    ...mapActions("settings/maintenance", [
      "getMaintenanceConfig",
      "updateMaintenanceConfig",
    ]),
    async fetchConfig() {
      const data = await this.getMaintenanceConfig();
      if (data?.value) {
        this.maintenanceMode = data.value.isMaintenance ?? false;
      }
    },
    onToggle(field, value) {
      // Revert the toggle immediately; apply only after password confirm
      this[field] = !value;
      this.confirmModal = {
        show: true,
        field,
        nextValue: value,
        label: value
          ? `enable ${field === "maintenanceMode" ? "Maintenance Mode" : "Debug Mode"}`
          : `disable ${field === "maintenanceMode" ? "Maintenance Mode" : "Debug Mode"}`,
        password: "",
      };
    },
    async applyToggle() {
      if (!this.confirmModal.password) return;
      const { field, nextValue } = this.confirmModal;
      if (field === "maintenanceMode") {
        const result = await this.updateMaintenanceConfig({
          isMaintenance: nextValue,
        });
        if (result) {
          this.maintenanceMode = nextValue;
          this.showToastSuccess(
            "Maintenance Mode Updated",
            `Maintenance mode has been ${nextValue ? "enabled" : "disabled"}.`
          );
        }
      }
      this.confirmModal.show = false;
      this.confirmModal.password = "";
    },
    cancelToggle() {
      this.confirmModal.show = false;
      this.confirmModal.password = "";
    },
  },
  setup() {
    const { showToastSuccess } = useToastHandler();
    return { showToastSuccess };
  },
};
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

/* Applied to sections that have no API backing */
.section-disabled {
  opacity: 0.45;
  pointer-events: none;
  user-select: none;
}
</style>
